// client/src/components/PublicProfile/PublicBadges.js
import React, { useState, useEffect } from 'react';
import { 
  Award, Star, Trophy, Target, AlertTriangle, Crown, 
  Filter, Grid, List, ChevronDown, ChevronUp 
} from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const PublicBadges = ({ profileData, t }) => {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showProgress, setShowProgress] = useState(false);
  const [expandedBadge, setExpandedBadge] = useState(null);

  useEffect(() => {
    loadBadges();
  }, [profileData]);

  const loadBadges = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Try multiple endpoints in case the route is different
      let response;
      let endpoint;
      
      try {
        endpoint = `/api/public-profile/${profileData.username}/badges`;
        response = await axios.get(endpoint);
      } catch (firstError) {
        try {
          endpoint = `/api/users/${profileData._id}/badges`;
          response = await axios.get(endpoint);
        } catch (secondError) {
          try {
            endpoint = `/api/badges/user/${profileData.username}`;
            response = await axios.get(endpoint);
          } catch (thirdError) {
            throw firstError; // Throw the original error
          }
        }
      }
      
      // Handle different response formats
      let processedBadgeData;
      
      if (response.data.badges || response.data.statistics) {
        // Expected format with nested structure
        processedBadgeData = response.data;
      } else if (Array.isArray(response.data)) {
        // Simple array of badges
        processedBadgeData = {
          badges: response.data,
          statistics: {
            totalEarned: response.data.length,
            totalAvailable: 100, // Default value
            completionPercentage: Math.min(100, (response.data.length / 100) * 100),
            categoriesUnlocked: [...new Set(response.data.map(b => b.category))].length,
            totalExpFromBadges: response.data.reduce((sum, b) => sum + (b.expReward || 0), 0)
          },
          recentBadges: response.data.slice(-5).reverse(),
          availableBadges: [],
          categorizedBadges: groupBadgesByCategory(response.data)
        };
      } else {
        // Unexpected format, create minimal structure
        processedBadgeData = {
          badges: [],
          statistics: {
            totalEarned: 0,
            totalAvailable: 100,
            completionPercentage: 0,
            categoriesUnlocked: 0,
            totalExpFromBadges: 0
          },
          recentBadges: [],
          availableBadges: [],
          categorizedBadges: {}
        };
      }
      
      setBadgeData(processedBadgeData);
      
    } catch (error) {
      console.error('Failed to load badges:', error);
      
      if (error.response?.status === 403) {
        setError('Badge information is private');
      } else if (error.response?.status === 404) {
        setError('Badge system not available');
      } else {
        setError(error.response?.data?.message || 'Failed to load badges');
      }
      
      // Don't show toast for common cases
      if (error.response?.status !== 403 && error.response?.status !== 404) {
        toast.error('Failed to load badges');
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper function to group badges by category
  const groupBadgesByCategory = (badges) => {
    return badges.reduce((groups, badge) => {
      const category = badge.category || 'other';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(badge);
      return groups;
    }, {});
  };

  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      'win_streak': t('winStreak', 'Win Streak'),
      'filler': t('filler', 'Filler'),
      'total_wins': t('totalWins', 'Total Wins'),
      'points_winning': t('pointsWinning', 'Points Winning'),
      'points_purchase': t('pointsPurchase', 'Points Purchase'),
      'multiplayer': 'Multiplayer',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'Multiplayer Wins',
      'battle_royale_wins': 'Battle Royale Wins',
      'other': t('other', 'Other')
    };
    return categoryNames[category] || category;
  };

  const getRarityInfo = (rarity) => {
    const rarityMap = {
      'common': { 
        color: '#6b7280', 
        name: t('common', 'Common'),
        glow: false
      },
      'rare': { 
        color: '#3b82f6', 
        name: t('rare', 'Rare'),
        glow: true
      },
      'epic': { 
        color: '#8b5cf6', 
        name: t('epic', 'Epic'),
        glow: true
      },
      'legendary': { 
        color: '#f59e0b', 
        name: t('legendary', 'Legendary'),
        glow: true
      }
    };
    return rarityMap[rarity] || rarityMap['common'];
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('unknown', 'Unknown');
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getBadgesToDisplay = () => {
    if (!badgeData || !badgeData.badges) return [];

    let badges = showProgress ? 
      [...(badgeData.badges || []), ...(badgeData.availableBadges?.slice(0, 6) || [])] : 
      (badgeData.badges || []);

    // Filter by category
    if (selectedCategory !== 'all') {
      badges = badges.filter(badge => badge.category === selectedCategory);
    }

    return badges;
  };

  const getCategories = () => {
    if (!badgeData || !badgeData.categorizedBadges) return [];
    return Object.keys(badgeData.categorizedBadges);
  };

  if (loading) {
    return (
      <div className="public-badges">
        <div className="badges-header">
          <h3 className="section-title">
            <Award className="w-5 h-5" />
            {t('badgeCollection', 'Badge Collection')}
          </h3>
        </div>
        
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>{t('loadingBadges', 'Loading badges...')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-badges">
        <div className="badges-header">
          <h3 className="section-title">
            <Award className="w-5 h-5" />
            {t('badgeCollection', 'Badge Collection')}
          </h3>
        </div>
        
        <div className="error-state">
          <AlertTriangle className="w-12 h-12 text-red-400 mb-4" />
          <h4 className="text-lg font-semibold text-red-400 mb-2">{error}</h4>
          <p className="text-gray-400 mb-4">
            {error === 'Badge information is private' 
              ? t('badgesPrivateDesc', 'This player has made their badge collection private.')
              : error === 'Badge system not available'
              ? t('badgeSystemNotAvailable', 'The badge system is not available for this profile.')
              : t('badgesLoadError', 'There was an error loading the badge collection.')
            }
          </p>
          
          <button onClick={loadBadges} className="retry-button mt-4">
            {t('retry', 'Retry')}
          </button>
        </div>
      </div>
    );
  }

  const badgesToDisplay = getBadgesToDisplay();
  const categories = getCategories();

  return (
    <div className="public-badges">
      {/* Header with Statistics */}
      <div className="badges-header">
        <div className="header-main">
          <h3 className="section-title">
            <Award className="w-5 h-5" />
            {t('badgeCollection', 'Badge Collection')}
          </h3>
          
          <div className="badge-stats">
            <div className="badge-stat">
              <span className="stat-value">{badgeData.statistics?.totalEarned || 0}</span>
              <span className="stat-label">{t('earned', 'Earned')}</span>
            </div>
            <div className="badge-stat">
              <span className="stat-value">{badgeData.statistics?.completionPercentage || 0}%</span>
              <span className="stat-label">{t('complete', 'Complete')}</span>
            </div>
            <div className="badge-stat">
              <span className="stat-value">{badgeData.statistics?.categoriesUnlocked || 0}</span>
              <span className="stat-label">{t('categories', 'Categories')}</span>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="completion-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${badgeData.statistics?.completionPercentage || 0}%` }}
            ></div>
          </div>
          <span className="progress-text">
            {badgeData.statistics?.totalEarned || 0} / {badgeData.statistics?.totalAvailable || 0} {t('badges', 'badges')}
          </span>
        </div>
      </div>

      {/* Controls */}
      <div className="badges-controls">
        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="all">{t('allCategories', 'All Categories')}</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {getCategoryDisplayName(category)}
              </option>
            ))}
          </select>

          {badgeData.availableBadges && badgeData.availableBadges.length > 0 && (
            <button
              onClick={() => setShowProgress(!showProgress)}
              className={`show-progress-btn ${showProgress ? 'active' : ''}`}
            >
              <Target className="w-4 h-4" />
              {t('showProgress', 'Show Progress')}
            </button>
          )}
        </div>

        <div className="view-controls">
          <button
            onClick={() => setViewMode('grid')}
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
          >
            <Grid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Recent Badges Section */}
      {badgeData.recentBadges && badgeData.recentBadges.length > 0 && (
        <div className="recent-badges-section">
          <h4 className="subsection-title">
            <Star className="w-4 h-4" />
            {t('recentlyEarned', 'Recently Earned')}
          </h4>
          <div className="recent-badges-list">
            {badgeData.recentBadges.map((badge, index) => {
              const rarityInfo = getRarityInfo(badge.rarity);
              return (
                <div 
                  key={badge.badgeId || badge._id || index} 
                  className="recent-badge-item"
                  style={{ borderColor: rarityInfo.color }}
                >
                  <div className="badge-icon-large">
                    {badge.icon || '🏅'}
                  </div>
                  <div className="recent-badge-info">
                    <h5 className="badge-name">{badge.badgeName || badge.name}</h5>
                    <p className="badge-description">{badge.description}</p>
                    <div className="badge-meta">
                      <span className="badge-date">{formatDate(badge.unlockedAt)}</span>
                      <span className="badge-exp">+{badge.expReward || 0} EXP</span>
                    </div>
                  </div>
                  <div 
                    className="rarity-indicator"
                    style={{ backgroundColor: rarityInfo.color }}
                  >
                    {rarityInfo.name}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Badges Grid/List */}
      <div className="badges-content">
        {badgesToDisplay.length === 0 ? (
          <div className="empty-state">
            <Award className="w-12 h-12 text-gray-400 mb-4" />
            <h4 className="text-lg font-semibold text-gray-300 mb-2">
              {selectedCategory === 'all' 
                ? t('noBadges', 'No Badges Yet')
                : t('noBadgesInCategory', 'No Badges in This Category')
              }
            </h4>
            <p className="text-gray-400">
              {t('noBadgesDesc', 'Badges will appear here as they are earned through gameplay.')}
            </p>
            
            {/* Show some sample badges if completely empty */}
            {(!badgeData.badges || badgeData.badges.length === 0) && (
              <div className="sample-badges mt-4">
                <p className="text-gray-500 text-sm mb-2">{t('sampleBadges', 'Sample badges that can be earned:')}</p>
                <div className="flex gap-2 justify-center">
                  <div className="sample-badge">🏆 {t('firstWin', 'First Win')}</div>
                  <div className="sample-badge">🔥 {t('winStreak', 'Win Streak')}</div>
                  <div className="sample-badge">⭐ {t('points', 'Points')}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className={`badges-display ${viewMode}`}>
            {badgesToDisplay.map((badge, index) => {
              const isProgress = !badge.unlockedAt && badge.progressPercentage !== undefined;
              const isEarned = badge.unlockedAt || badge.badgeId || badge._id;
              const rarityInfo = getRarityInfo(badge.rarity);
              const isExpanded = expandedBadge === (badge.badgeId || badge._id || index);

              return (
                <div
                  key={badge.badgeId || badge._id || index}
                  className={`badge-item ${badge.rarity || 'common'} ${isProgress ? 'progress' : 'earned'} ${viewMode}`}
                  onClick={() => setExpandedBadge(isExpanded ? null : (badge.badgeId || badge._id || index))}
                >
                  <div className="badge-main">
                    <div className="badge-visual">
                      <div className="badge-icon">
                        {badge.icon || '🏅'}
                      </div>
                      {isProgress && (
                        <div className="progress-overlay">
                          <div 
                            className="progress-circle"
                            style={{
                              background: `conic-gradient(${rarityInfo.color} ${(badge.progressPercentage || 0) * 3.6}deg, transparent 0deg)`
                            }}
                          >
                            <div className="progress-center">
                              {Math.round(badge.progressPercentage || 0)}%
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="badge-info">
                      <h5 className="badge-name">{badge.badgeName || badge.name}</h5>
                      {viewMode === 'list' && (
                        <p className="badge-description">{badge.description}</p>
                      )}
                      
                      <div className="badge-meta">
                        <span 
                          className="badge-rarity"
                          style={{ color: rarityInfo.color }}
                        >
                          {rarityInfo.name}
                        </span>
                        {badge.category && (
                          <span className="badge-category">
                            {getCategoryDisplayName(badge.category)}
                          </span>
                        )}
                      </div>

                      {isEarned && badge.unlockedAt && (
                        <div className="badge-earned-info">
                          <Trophy className="w-3 h-3" />
                          <span>{t('earned', 'Earned')} {formatDate(badge.unlockedAt)}</span>
                        </div>
                      )}

                      {isProgress && (
                        <div className="badge-progress-info">
                          <Target className="w-3 h-3" />
                          <span>
                            {badge.currentProgress || 0} / {badge.requirement || 0}
                            {badge.remaining > 0 && ` (${badge.remaining} ${t('remaining', 'remaining')})`}
                          </span>
                        </div>
                      )}

                      <div className="badge-reward">
                        <Star className="w-3 h-3" />
                        <span>+{badge.expReward || 0} EXP</span>
                      </div>
                    </div>

                    <div className="badge-expand">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="badge-details">
                      <div className="badge-description-full">
                        {badge.description}
                      </div>
                      
                      <div className="badge-requirements">
                        <h6>{t('requirement', 'Requirement')}</h6>
                        <p>{badge.requirement?.toLocaleString() || 0}</p>
                      </div>

                      {isProgress && (
                        <div className="progress-details">
                          <div className="progress-bar-detailed">
                            <div 
                              className="progress-fill"
                              style={{ 
                                width: `${Math.min(100, badge.progressPercentage || 0)}%`,
                                backgroundColor: rarityInfo.color
                              }}
                            ></div>
                          </div>
                          <div className="progress-text">
                            {Math.round(badge.progressPercentage || 0)}% {t('complete', 'Complete')}
                          </div>
                        </div>
                      )}

                      {isEarned && badge.unlockedAt && (
                        <div className="earned-details">
                          <div className="earned-date">
                            <strong>{t('unlockedOn', 'Unlocked On')}: </strong>
                            {formatDate(badge.unlockedAt)}
                          </div>
                          <div className="earned-exp">
                            <strong>{t('expGained', 'EXP Gained')}: </strong>
                            +{badge.expReward || 0}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Rarity Glow Effect */}
                  {rarityInfo.glow && isEarned && (
                    <div 
                      className="rarity-glow"
                      style={{ 
                        boxShadow: `0 0 20px ${rarityInfo.color}40`
                      }}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Category Breakdown */}
      {badgeData.categorizedBadges && Object.keys(badgeData.categorizedBadges).length > 0 && (
        <div className="category-breakdown">
          <h4 className="subsection-title">
            <Filter className="w-4 h-4" />
            {t('categoryBreakdown', 'Category Breakdown')}
          </h4>
          <div className="category-grid">
            {Object.entries(badgeData.categorizedBadges).map(([category, badges]) => (
              <div key={category} className="category-card">
                <div className="category-header">
                  <h5 className="category-name">{getCategoryDisplayName(category)}</h5>
                  <span className="category-count">{badges.length}</span>
                </div>
                <div className="category-badges">
                  {badges.slice(0, 3).map((badge, index) => (
                    <div key={index} className="mini-badge">
                      {badge.icon || '🏅'}
                    </div>
                  ))}
                  {badges.length > 3 && (
                    <div className="mini-badge more">
                      +{badges.length - 3}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {badgeData.statistics && (
        <div className="badges-summary">
          <div className="summary-card">
            <h4 className="summary-title">
              <Crown className="w-4 h-4" />
              {t('achievementSummary', 'Achievement Summary')}
            </h4>
            <div className="summary-content">
              <p>
                <strong>{profileData.inGameName}</strong> {t('badgeSummaryText', 'has earned {earned} out of {total} available badges ({percentage}% completion). They have unlocked {categories} different categories and gained a total of {exp} experience points from badges.')
                  .replace('{earned}', badgeData.statistics.totalEarned || 0)
                  .replace('{total}', badgeData.statistics.totalAvailable || 0)
                  .replace('{percentage}', badgeData.statistics.completionPercentage || 0)
                  .replace('{categories}', badgeData.statistics.categoriesUnlocked || 0)
                  .replace('{exp}', (badgeData.statistics.totalExpFromBadges || 0).toLocaleString())}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicBadges;